import React from 'react';
import PropTypes from 'prop-types';
import { formatRelativeTime } from '../../utils/relativeTime';
import VendorName from './VendorName';
import './sandbox.css';

/** A session of an AI tool counts as active while its last call is this recent. */
export const ACTIVE_WINDOW_MS = 10 * 60 * 1000;

/**
 * Activity's "AI tools" sessions (sandbox-first, docs/SANDBOX_FIRST.md), from vfs-service `GET /api/v1/me/activity`
 * (`sessions: [{ via, projectId, projectName, start, end, changes, lastMessage }]`), split into the Kanban's Active
 * column (the last call within ten minutes) and Recent, newest first.
 */
export function splitToolSessions(sessions, now = Date.now()) {
  const sorted = [...(sessions || [])]
    .filter((session) => session && session.projectId)
    .sort((a, b) => new Date(b.end || b.start || 0) - new Date(a.end || a.start || 0));
  const isActive = (session) => {
    const end = new Date(session.end || session.start || 0).getTime();
    return Number.isFinite(end) && now - end <= ACTIVE_WINDOW_MS;
  };
  return { active: sorted.filter(isActive), recent: sorted.filter((session) => !isActive(session)) };
}

/** A stable key for one session. */
export const toolSessionKey = (session) => `${session.via || 'tool'}:${session.projectId}:${session.start || session.end || ''}`;

/** One AI tool session as a Kanban card: the project, the tool, how many changes and when, and where to see them. */
export default function ToolSessionCard({ session, onOpen }) {
  const changes = Number(session.changes) || 0;
  const meta = [`${changes} change${changes === 1 ? '' : 's'}`, formatRelativeTime(session.end || session.start)].filter(Boolean).join(' · ');
  return (
    <div className="session-kanban-card sbx-tool-session">
      <div className="session-kanban-card-name">{session.projectName || session.projectId}</div>
      <div className="sbx-tool-session-meta">
        <span className="sbx-pill sbx-pill-tool">{session.via ? <VendorName name={session.via} size={12} /> : 'AI tool'}</span>
        <span>{meta}</span>
      </div>
      {session.lastMessage && <div className="sbx-tool-session-last">{session.lastMessage}</div>}
      <button className="sbx-link" onClick={() => onOpen(session)} type="button">See the changes</button>
    </div>
  );
}

ToolSessionCard.propTypes = {
  session: PropTypes.shape({
    via: PropTypes.string,
    projectId: PropTypes.string.isRequired,
    projectName: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
    changes: PropTypes.number,
    lastMessage: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};
